import { useState } from "react";
import {
  CircleNotchIcon,
  MapPinIcon,
  MapTrifoldIcon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import { useManualLocation } from "@/state/manualLocation";
import { trackBusinessLocationChange } from "@/analytics/onboardingAnalytics";
import OnboardingModal from "../components/OnboardingModal";
import OnboardingPrimaryButton from "../components/OnboardingPrimaryButton";
import OnboardingStepHeader, {
  ONBOARDING_STEP_HEADING_ID,
} from "../components/OnboardingStepHeader";
import { useBusinessCity } from "../state/useBusinessCity";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";
import type { OnboardingCity } from "../types";
import CityStep from "./CityStep";

/**
 * Business branch, the last step — "Let’s explore!", ported from native's
 * `BusinessCityScreen` (`15666:346394`).
 *
 * Where the individual path's city step asks outright, here the claim already
 * says where the business is, so its city is proposed for the visitor to
 * confirm with Continue, or swap with **Change**.
 *
 * Change opens the ordinary city picker in the same sheet, in its pick-only
 * mode: a pick comes back here as the new proposal rather than finishing the
 * flow, and ✕ returns with the proposal untouched.
 *
 * A claim that never resolved to a place (a name kept as typed) has nothing to
 * derive a city from, so the step opens straight on the picker instead of
 * proposing nothing.
 */
const BusinessCityStep = () => {
  const i18n = useI18N();
  const { answers, completeWithCity, dismiss } = useOnboardingFlow();
  const { stepNumber, totalSteps } = useOnboardingStepNavigation();
  const { setManualLocation } = useManualLocation();

  const { city: derivedCity, isLoading } = useBusinessCity(
    answers.businessClaim?.business,
  );

  /** A city picked through Change, which overrides the derived one. */
  const [chosenCity, setChosenCity] = useState<OnboardingCity | null>(null);
  const [isPicking, setIsPicking] = useState(false);

  const city = chosenCity ?? derivedCity;
  const title = i18n.get("onboardingBusinessCityTitle");

  /** Same commit as `CityStep`: point the site at the city, then finish. */
  const complete = (selected: OnboardingCity) => {
    if (selected.latitude != null && selected.longitude != null) {
      setManualLocation({
        latitude: selected.latitude.toString(),
        longitude: selected.longitude.toString(),
        label: selected.label || selected.name,
        source: "city",
      });
    }
    completeWithCity(selected);
  };

  if (isPicking || (!isLoading && !city)) {
    const pickerTitle = city
      ? i18n.get("onboardingBusinessCityChangeTitle")
      : i18n.get("onboardingBusinessCityAskTitle");

    return (
      <CityStep
        ariaLabel={pickerTitle}
        onPick={(picked) => {
          setChosenCity(picked);
          setIsPicking(false);
        }}
        // Back to the proposal from the search field, only when there is one
        // to go back to.
        onBack={city ? () => setIsPicking(false) : undefined}
        backLabel={
          city
            ? i18n
                .get("onboardingBusinessCityCloseLabel")
                .replace("{city}", city.name)
            : undefined
        }
        header={
          <h2
            id={ONBOARDING_STEP_HEADING_ID}
            tabIndex={-1}
            className="title-s-semibold text-fg-neutral-strong outline-none"
          >
            {pickerTitle}
          </h2>
        }
      />
    );
  }

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <OnboardingPrimaryButton
          label={i18n.get("continue")}
          disabled={!city}
          onClick={() => city && complete(city)}
        />
      }
    >
      {isLoading || !city ? (
        <div className="flex min-h-full items-center justify-center">
          {/* The modal's focus trap opens on this id; the real heading
              arrives with the city. */}
          <h2 id={ONBOARDING_STEP_HEADING_ID} tabIndex={-1} className="sr-only">
            {title}
          </h2>
          <CircleNotchIcon
            size={24}
            aria-label={i18n.get("loading")}
            className="animate-spin text-fg-brand-strong"
          />
        </div>
      ) : (
        <div className="flex min-h-full flex-col justify-center gap-4">
          <MapTrifoldIcon
            size={36}
            aria-hidden="true"
            className="mx-auto text-fg-neutral-strong"
          />

          <OnboardingStepHeader
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            title={title}
            subtitle={i18n.get("onboardingBusinessCityBody")}
            align="center"
          />

          <div className="flex min-h-[60px] items-center gap-3 rounded-xl border border-border-token-neutral-minimal px-4 py-3">
            <MapPinIcon
              size={24}
              aria-hidden="true"
              className="shrink-0 text-fg-neutral-strong"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="body-base-medium truncate text-fg-neutral-strong">
                {city.name}
              </span>
              {city.country ? (
                <span className="body-s-regular truncate text-fg-neutral-subtle">
                  {city.country}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => {
                trackBusinessLocationChange();
                setIsPicking(true);
              }}
              aria-label={i18n
                .get("onboardingBusinessCityChangeLabel")
                .replace("{city}", city.name)}
              className="focus-blue body-m-medium min-h-6 shrink-0 rounded-lg text-fg-brand-strong underline"
            >
              {i18n.get("onboardingBusinessCityChange")}
            </button>
          </div>
        </div>
      )}
    </OnboardingModal>
  );
};

export default BusinessCityStep;
